"use client";

import { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light";

type ThemeProviderProps = {
  children: React.ReactNode;
};

type ThemeProviderState = {
  theme: Theme;
  toggleTheme: () => void;
};

const ThemeProviderContext = createContext<ThemeProviderState | undefined>(undefined);

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [mounted, setMounted] = useState(false);
  const theme: Theme = "light";

  // Listen for system theme changes
  useEffect(() => {
    // TEMPORARILY DISABLED: No need to listen for system theme changes
  }, []);

  // Handle mounted state
  useEffect(() => {
    setMounted(true);
  }, []);

  // Update DOM and localStorage when component mounts
  useEffect(() => {
    const root = document.documentElement;
    // TEMPORARILY DISABLED: Always use light mode
    root.classList.remove("dark");
    // Update color scheme meta tag for light mode
    document.querySelector('meta[name="color-scheme"]')?.remove();
    const meta = document.createElement("meta");
    meta.name = "color-scheme";
    meta.content = "light";
    document.head.appendChild(meta);

    // Force light mode in localStorage
    localStorage.setItem("theme", "light");
  }, []); // Only run once on mount

  const toggleTheme = () => {
    // TEMPORARILY DISABLED: Toggle functionality is disabled
    // Just log a message to console
    console.log("Theme toggle is temporarily disabled. Using light mode only.");
  };

  if (!mounted) {
    return null;
  }

  return (
    <ThemeProviderContext.Provider value={{ theme, toggleTheme }} data-oid="0zn37ns">
      {children}
    </ThemeProviderContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeProviderContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
